import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { ServerRegular, WrenchRegular } from '@fluentui/react-icons';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useWorkflowConnectors } from '@/hooks/useWorkflowConnectors';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { ExecutionOverlay } from '@/components/shared';
import {
  FloatingNode,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { resolveToolDisplay } from './toolDisplay';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { SubWorkflowToolNode } from './SubWorkflowToolNode/SubWorkflowToolNode';

/**
 * Floating Tool node data structure
 */
export type FloatingToolNodeData = {
  // Tool identification
  toolName: string;
  label: string;
  description?: string;
  parentAgentId: string;
  parentAgentName?: string;

  // Connector info for fetching visuals from manifest
  connectorId?: string;
  operationId?: string;

  // Action info
  actionName?: string;
  actionType?: string;

  /** AgentTool.type discriminator (e.g. 'McpClientTool', 'subworkflow'). Drives MCP styling. */
  toolKind?: string;

  // Connection status
  connectionStatus?: 'connected' | 'disconnected' | 'warning';
  connectionName?: string;

  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  /** True if tool was not invoked in the current iteration (show as skipped) */
  wasNotInvoked?: boolean;

  // Sub-workflow specific
  toolNodeId?: string;
  isSubWorkflowExpanded?: boolean;
  onSubWorkflowExpandToggle?: (toolNodeId: string, expanded: boolean) => void;
  subWorkflowStepCount?: number;
};

export type FloatingToolNodeType = Node<FloatingToolNodeData, 'floatingTool'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  connectorIcon: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
  },
  skippedWrapper: {
    position: 'relative',
    opacity: 0.5,
  },
});

/**
 * Default (non-workflow) tool node rendering.
 * Extracted so FloatingToolNode can dispatch without conditional hooks.
 */
const DefaultToolNode = memo(
  ({ data, selected, id }: { data: FloatingToolNodeData; selected?: boolean; id: string }) => {
    const styles = useStyles();
    const detailLevel = useDetailLevel();
    const { getConnectorInfo, isLoading: isConnectorLoading } = useWorkflowConnectors();

    // Get connection validation status (includes mock detection)
    const { validationResult } = useNodeConnectionStatus({
      nodeId: id,
      operationType: data.actionType as string,
      connectionReferenceKey: data.connectionName,
    });

    // Resolve icon / brand color from the connector manifest (by connection
    // name or namespaced action name) and fall back to the static built-in
    // manifest so control-flow / built-in operations match the canvas.
    const display = resolveToolDisplay(
      {
        connectionName: data.connectionName,
        actionName: data.actionName,
        actionType: data.actionType,
      },
      getConnectorInfo,
    );

    const isMcpTool = data.toolKind === 'McpClientTool';
    const iconUri = display.iconUri;
    const brandColor = display.brandColor;
    const hasIcon = Boolean(iconUri);
    // Show a spinner while connectors are still loading and we haven't yet
    // matched one — even if a built-in fallback exists — to avoid flicker
    // when the connector lookup eventually wins.
    const isManifestLoading = isConnectorLoading && display.source !== 'connector';

    const isMonitoringMode = data.executionStatus !== undefined || data.wasNotInvoked;
    const isSkipped = data.wasNotInvoked;

    // Map connection/execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (isSkipped) {
        return 'default'; // Skipped tools use opacity instead of border color
      }

      if (!data.executionStatus) {
        // Check validation status when not in monitoring mode
        if (validationResult) {
          if (validationResult.status === 'mock-active') return 'mock';
          if (!validationResult.isValid) {
            return validationResult.status === 'warning' ? 'warning' : 'error';
          }
        }
        // Fall back to connection status for backwards compatibility
        if (data.connectionStatus === 'disconnected') {
          return 'error';
        }
        if (data.connectionStatus === 'warning') {
          return 'warning';
        }
        return 'default';
      }

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        case 'pending':
          return 'default';
        default:
          return 'default';
      }
    };

    // Build status message for error/mock badge tooltip
    const statusMessage =
      validationResult?.status === 'mock-active'
        ? 'Using test data'
        : validationResult && !validationResult.isValid
          ? validationResult.error?.message ?? 'Connection not configured'
          : data.connectionStatus === 'disconnected'
            ? 'Connection required'
            : data.connectionStatus === 'warning'
              ? 'Connection warning'
              : undefined;

    // Render the icon
    const renderIcon = () => {
      if (isManifestLoading) {
        return <Spinner size="extra-tiny" aria-label="Loading connector info" />;
      }

      if (hasIcon) {
        return (
          <ConnectorIcon
            iconUri={iconUri!}
            size={20}
          />
        );
      }

      return isMcpTool ? <ServerRegular /> : <WrenchRegular />;
    };

    const toolLabel = data.label || data.toolName;

    return (
      <div className={isSkipped ? styles.skippedWrapper : styles.wrapper}>
        <FloatingNode
          icon={renderIcon()}
          label={toolLabel}
          detailLevel={detailLevel}
          selected={selected ?? false}
          status={getNodeStatus()}
          statusMessage={statusMessage}
          accentColor={isMcpTool ? 'marigold' : 'brand'}
          {...(!isMcpTool && brandColor ? { brandColorOverride: brandColor } : {})}
          shape="circle"
          ariaLabel={`${isMcpTool ? 'MCP tool' : 'Tool'}: ${toolLabel}${data.connectionStatus === 'disconnected' ? ', connection required' : ''}`}
        >
          {/* Tool input at bottom - connects from agent's top handle (tools above agent) */}
          <Handle
            type="target"
            position={Position.Bottom}
            id="input"
            isConnectable={false}
            style={{ opacity: 0 }}
          />
        </FloatingNode>

        {/* Show execution overlay in monitoring mode */}
        {isMonitoringMode && !isSkipped && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
            size="small"
          />
        )}
      </div>
    );
  }
);

DefaultToolNode.displayName = 'DefaultToolNode';

/**
 * Floating-style Tool Node — dispatches to SubWorkflowToolNode or DefaultToolNode.
 * No hooks here so React's reconciler doesn't hit the conditional-return static flag issue.
 */
export const FloatingToolNode = memo(
  ({ data, selected, id }: NodeProps<FloatingToolNodeType>) => {
    if (data.actionType === 'Workflow') {
      return <SubWorkflowToolNode data={data} selected={selected} id={id} />;
    }
    return <DefaultToolNode data={data} selected={selected} id={id} />;
  }
);

FloatingToolNode.displayName = 'FloatingToolNode';
